import { ZCDCard } from '@zencity/common-ui';
import { LoaderMask } from 'components/LoaderMask/LoaderMask';
import { useAsyncEffect } from 'customHooks/hooks';
import React, { ReactElement, useContext, useState } from 'react';
import { WidgetGeneratorContext } from 'screens/WidgetGenerator/contexts/WidgetGeneratorContext';
import { logger } from 'utils/logger';
import { getAnswerClassificationSubTopicBreakdown } from 'services/answerClassificationBreakdown';
import { getPreviousCycleDates } from 'screens/WidgetGenerator/utils/cycles';
import {
  useDivisionIdsToFilterBy,
  useGroupByDepthLevel,
} from 'screens/WidgetGenerator/reportGeneratorComponents/hooks/useDivisionIdsToFilterBy';
import {
  extractSortedTopConcerns,
  SortedTopConcern,
} from 'screens/WidgetGenerator/utils/answerClassificationBreakdown';
import { ReportBuilderWidgetProps } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import { TopConcernsChart } from 'screens/WidgetGenerator/reportGeneratorComponents/components/ClassificationTopConcernsHOC/TopConcernsChart';
import styles from './ClassificationTopConcernsHOC.module.scss';

const MAX_TOPICS_ITEMS = 12;

export const ClassificationTopConcernsHOC = (props: ReportBuilderWidgetProps): ReactElement => {
  const { params, clientData } = props;
  const { filters } = params;
  const selectedQuestionId = filters?.classifiedOpenEndedGenericQuestion;

  const { selectedSurveyGroup, selectedDateRange, cycles, client } = useContext(WidgetGeneratorContext);
  const [sortedTopConcerns, setSortedTopConcerns] = useState<SortedTopConcern[]>([]);
  const [displayTrend, setDisplayTrend] = useState<boolean>(false);

  const divisionIdsToFilterBy = useDivisionIdsToFilterBy({ filters, clientData });
  const groupByDepthLevel = useGroupByDepthLevel({ filters, clientData });

  const fetchData = async () => {
    if (!selectedQuestionId) {
      return undefined;
    }

    let previousCycleTopConcerns: SortedTopConcern[] | undefined;
    const previousCycleDateRange = getPreviousCycleDates(selectedDateRange, cycles);
    if (previousCycleDateRange) {
      setDisplayTrend(true);
      const previousCycleResponse = await getAnswerClassificationSubTopicBreakdown(client.zc_client_id, {
        survey_group: selectedSurveyGroup.id,
        generic_question: selectedQuestionId,
        start_date: previousCycleDateRange.startDate,
        end_date: previousCycleDateRange.endDate,
        division: divisionIdsToFilterBy,
        group_by_depth_level: groupByDepthLevel,
      });
      previousCycleTopConcerns = extractSortedTopConcerns(previousCycleResponse);
    } else {
      setDisplayTrend(false);
    }

    const response = await getAnswerClassificationSubTopicBreakdown(client.zc_client_id, {
      survey_group: selectedSurveyGroup.id,
      generic_question: selectedQuestionId,
      start_date: selectedDateRange.startDate,
      end_date: selectedDateRange.endDate,
      division: divisionIdsToFilterBy,
      group_by_depth_level: groupByDepthLevel,
    });
    setSortedTopConcerns(extractSortedTopConcerns(response, previousCycleTopConcerns));
    return response;
  };

  const { fetchedData, isLoading, error } = useAsyncEffect(fetchData, [
    selectedSurveyGroup,
    selectedQuestionId,
    selectedDateRange,
    cycles,
    divisionIdsToFilterBy,
  ]);

  if (error) {
    logger.error(error);
  }

  const Header = (
    <>
      <div className={styles.headerTitle}>Top concerns</div>
      <div className={styles.headerBody}>
        {fetchedData?.total_classifications} labeled responses out of {fetchedData?.total_responses}
      </div>
    </>
  );

  return (
    <LoaderMask isLoading={isLoading}>
      <div className={styles.cardWrapper}>
        <ZCDCard header={Header}>
          {!selectedQuestionId && <h3>Please select a question</h3>}
          {selectedQuestionId && fetchedData?.total_classifications && sortedTopConcerns.length > 0 && (
            <TopConcernsChart
              sortedTopConcern={sortedTopConcerns.slice(0, MAX_TOPICS_ITEMS)}
              displayTrend={displayTrend}
            />
          )}
        </ZCDCard>
      </div>
    </LoaderMask>
  );
};
